/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
} from "@mui/material";
import api from "../../api/index.js";
import ClearIcon from "@mui/icons-material/Clear";
import { LoadingSpinner } from "../loading_spinner";
import { AbilityContext } from "../../contexts/abilityContext";
import { strictCanScope } from "../../utils/caslStrict";
import {
  StyledDialogTitle,
  StyledFieldLabel,
  StyledReadOnlyField,
  StyledEditField,
  CrossIconContainer,
  StyledButton,
  StyledRestoreButton,
  FieldPair,
  scrollableContent,
  languageSelectorBox,
  fixedActionContainer,
  createStyledReadOnlyFieldProps,
  StyledSubTitle,
} from "./styles";
import { createSpanishFieldProps, isEnglishValueBlank } from "./utils";

export const TranslationModal = ({
  open,
  onClose,
  restaurantData,
  onSave,
  isLoading = false,
  onLoadingChange,
}) => {
  const { ability } = useContext(AbilityContext);
  const canManageTranslations = strictCanScope(ability, "update", "Restaurant", `${restaurantData?.id}`);
  const canViewTranslations = strictCanScope(ability, "read", "Restaurant", `${restaurantData?.id}`);
  const isReadOnly = canViewTranslations && !canManageTranslations;

  const [translations, setTranslations] = useState(null);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const previousRestaurantId = useRef(null);

  const resetTranslations = () => {
    setTranslations(null);
    previousRestaurantId.current = null;
  };

  useEffect(() => {
    if (!open || !restaurantData?.id) {
      return;
    }

    if (previousRestaurantId.current !== restaurantData.id) {
      previousRestaurantId.current = restaurantData.id;
      resetTranslations();
      loadTranslations();
    }
  }, [open, restaurantData?.id]);

  const loadTranslations = async () => {
    if (!restaurantData?.id) return;
    
    setIsLoadingData(true);
    onLoadingChange?.(true);
    
    try {
      const response = await api.get(`/v1/admin/restaurants/${restaurantData.id}`);
      const data = response?.data;
      
      setTranslations({
        description: data?.description || "",
        address: data?.address || "",
        cuisine: data?.cuisine || "",
        accessibility: data?.accessibility || "",
        dress_code: data?.dress_code || "",
        parking: data?.parking || "",
        payment_options: data?.payment_options || "",
        amount_per_guest: data?.amount_per_guest || "",
        spanish_description: data?.spanish_translation?.translated_description || "",
        spanish_address: data?.spanish_translation?.translated_address || "",
        spanish_cuisine_name: data?.spanish_translation?.translated_cuisine_name || "",
        spanish_accessibility: data?.restaurant_detail?.spanish_translation?.translated_accessibility || "",
        spanish_dress_code: data?.restaurant_detail?.spanish_translation?.translated_dress_code || "",
        spanish_parking: data?.restaurant_detail?.spanish_translation?.translated_parking || "",
        spanish_payment_options: data?.restaurant_detail?.spanish_translation?.translated_payment_options || "",
        spanish_amount_per_guest: data?.spanish_translation?.translated_amount_per_guest || "",
      });
    } catch (error) {
      console.error("Failed to load translations:", error);
    } finally {
      setIsLoadingData(false);
      onLoadingChange?.(false);
    }
  };

  const handleTranslationChange = (field, value) => {
    if (!translations) return;
    setTranslations({ ...translations, [field]: value });
  };

  // Spanish text with no English source is cleared rather than re-saved.
  const spanishValueToSave = (englishValue, spanishValue) =>
    isEnglishValueBlank(englishValue) ? "" : spanishValue;

  const handleSave = () => {
    if (!translations) return;
    onSave({
      ...restaurantData,
      spanish_description: spanishValueToSave(translations.description, translations.spanish_description),
      spanish_address: spanishValueToSave(translations.address, translations.spanish_address),
      spanish_cuisine_name: spanishValueToSave(translations.cuisine, translations.spanish_cuisine_name),
      spanish_accessibility: spanishValueToSave(translations.accessibility, translations.spanish_accessibility),
      spanish_dress_code: spanishValueToSave(translations.dress_code, translations.spanish_dress_code),
      spanish_parking: spanishValueToSave(translations.parking, translations.spanish_parking),
      spanish_payment_options: spanishValueToSave(translations.payment_options, translations.spanish_payment_options),
      spanish_amount_per_guest: spanishValueToSave(translations.amount_per_guest, translations.spanish_amount_per_guest)
    });
  };

  const handleRestoreDefaults = async () => {
    if (!restaurantData?.id || !translations) return;
    
    setIsLoadingDefaults(true);
    try {
      const response = await api.get(
        `/v1/admin/restaurants/${restaurantData.id}/fetch_default_translations`
      );
      const defaultTranslations = response?.data;

      setTranslations({
        ...translations,
        spanish_description: defaultTranslations?.spanish_description || "",
        spanish_address: defaultTranslations?.spanish_address || "",
        spanish_cuisine_name: defaultTranslations?.spanish_cuisine_name || "",
        spanish_accessibility: defaultTranslations?.spanish_accessibility || "",
        spanish_dress_code: defaultTranslations?.spanish_dress_code || "",
        spanish_parking: defaultTranslations?.spanish_parking || "",
        spanish_payment_options: defaultTranslations?.spanish_payment_options || "",
        spanish_amount_per_guest: defaultTranslations?.spanish_amount_per_guest || "",
      });
    } catch (error) {
      console.error("Failed to fetch default translations:", error);
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  const handleClose = () => {
    resetTranslations();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          padding: "0",
          display: "flex",
          flexDirection: "column",
          height: "90vh",
          overflow: "hidden",
        },
      }}
    >
      {isLoadingData || !translations ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <LoadingSpinner isLoading={true} />
        </Box>
      ) : (
        <>
          <CrossIconContainer>
            <ClearIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
          </CrossIconContainer>

          <StyledDialogTitle>
            Manage Translations - {restaurantData?.name || ""}
          </StyledDialogTitle>

          <DialogContent sx={scrollableContent}>
            <Box sx={languageSelectorBox}>
              <StyledSubTitle>
                Spanish Translation
              </StyledSubTitle>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Description</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.description}
                  disabled
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_description}
                  onChange={(e) =>
                    handleTranslationChange(
                      "spanish_description",
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  label="Español"
                  {...createSpanishFieldProps(translations.description, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Address</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.address}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_address}
                  onChange={(e) =>
                    handleTranslationChange("spanish_address", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.address, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Cuisine</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.cuisine}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_cuisine_name}
                  onChange={(e) =>
                    handleTranslationChange("spanish_cuisine_name", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.cuisine, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Accessibility</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.accessibility}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_accessibility}
                  onChange={(e) =>
                    handleTranslationChange(
                      "spanish_accessibility",
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.accessibility, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Dress Code</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.dress_code}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_dress_code}
                  onChange={(e) =>
                    handleTranslationChange("spanish_dress_code", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.dress_code, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Parking</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.parking}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_parking}
                  onChange={(e) =>
                    handleTranslationChange("spanish_parking", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.parking, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>Payment Options</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.payment_options}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_payment_options}
                  onChange={(e) =>
                    handleTranslationChange(
                      "spanish_payment_options",
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.payment_options, isReadOnly)}
                />
              </FieldPair>
            </Box>

            <Box sx={{ marginBottom: "0px" }}>
              <StyledFieldLabel>Amount per guest</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.amount_per_guest}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_amount_per_guest}
                  onChange={(e) =>
                    handleTranslationChange(
                      "spanish_amount_per_guest",
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.amount_per_guest, isReadOnly)}
                />
              </FieldPair>
            </Box>
          </DialogContent>

          <Box sx={fixedActionContainer}>
            <StyledRestoreButton
              onClick={handleRestoreDefaults}
              variant="outlined"
              disabled={isLoading || isReadOnly || isLoadingDefaults}
            >
              {isLoadingDefaults ? <LoadingSpinner isLoading={true} /> : "Restore Defaults"}
            </StyledRestoreButton>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <StyledButton
                onClick={handleClose}
                variant="outlined"
                disabled={isLoading}
              >
                Cancel
              </StyledButton>
              <StyledButton
                onClick={handleSave}
                variant="contained"
                disabled={isLoading || isReadOnly}
              >
                {isLoading ? <LoadingSpinner isLoading={true} /> : "Save"}
              </StyledButton>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  );
};
